import {Show} from 'platform/foundation';

import {useState} from 'react';

import {isNil, mergeAll} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {tiresInventoryRoutes} from '@dms/routes';
import {DmsDataGrid, TireSetDetail} from '@dms/shared';

import {composePath} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

export interface TiresDGProps {
  vehicleId?: string;
  customerId?: string;
  createNewLink?: string;
}

const PREDEFINED_COLUMNS = [
  'tireManufacturer',
  'tireSeason',
  'orderId',
  'orderSetId',
  'setNumber',
] as const;

export function TiresDG(props: TiresDGProps) {
  const [editTireSetData, setEditTireSetData] = useState<{
    orderId: string;
    setId: string;
    title: string;
    refreshData: () => void;
  } | null>(null);

  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([
      filter,
      {
        vehicleId: props.vehicleId ? props.vehicleId : undefined,
        customerId: props.customerId ? props.customerId : undefined,
      },
    ]);

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    getColumnData,
    refreshData,
  }) => {
    const tireManufacturer = getColumnData<{label: string}>('tireManufacturer')?.value?.label;
    const tireSeason = getColumnData<{label: string}>('tireSeason')?.value?.label;
    const orderId = getColumnData<string>('orderId')?.value;
    const orderSetId = getColumnData<string>('orderSetId')?.value;
    const setNumber = getColumnData<string>('setNumber')?.value;

    if (actionKey === 'redirectDetail') {
      const titleData = [tireManufacturer, tireSeason].filter(isNotNilOrEmpty);

      if (isNil(orderId) || isNil(orderSetId)) {
        throw new Error('orderId or orderSetId is undefined');
      }

      setEditTireSetData({
        orderId,
        setId: orderSetId,
        title: `${setNumber} - ${titleData.join(', ')}`,
        refreshData,
      });
    }

    if (actionKey === 'redirectTireOrder') {
      window.open(
        composePath(tiresInventoryRoutes.tireOrderDetail, {
          params: {id: orderId},
        }),
        '_blank'
      );
    }
  };

  return (
    <>
      <DmsDataGrid
        data-testid="tires-grid"
        gridCode="tire-order-wheel-service-case-creating"
        autoHeight
        actionCallback={actionCallback}
        predefinedColumns={PREDEFINED_COLUMNS}
        queryModifier={queryModifier}
      />

      <Show when={editTireSetData}>
        {(editTireSetData) => (
          <TireSetDetail
            tireOrderId={editTireSetData.orderId}
            tireSetId={editTireSetData.setId}
            onClose={() => setEditTireSetData(null)}
            onEdit={() => {
              editTireSetData.refreshData();
              setEditTireSetData(null);
            }}
          />
        )}
      </Show>
    </>
  );
}
